import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, chatSocketUrl, getToken } from '../api';
import type { ChatMessage, PublicProfile } from '../types';
import { Avatar } from '../components/Avatar';
import { loadLastResults } from '../lastResults';
import { Icon } from '../components/Icon';

const PING_MS = 30_000;
const RECONNECT_MS = 3_000;

/**
 * One DM thread. Live delivery over a WebSocket to the ChatRoom DO (join with
 * the JWT, receive history, then message events). If the socket is down the
 * composer falls back to the HTTP endpoint so nothing is lost.
 */
export function ChatPage() {
  const { roomId = '' } = useParams();
  const [me, setMe] = useState<string | null>(null);
  const [peer, setPeer] = useState<PublicProfile | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [status, setStatus] = useState<'connecting' | 'live' | 'offline'>('connecting');
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const last = loadLastResults();
  const ws = useRef<WebSocket | null>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const closed = useRef(false);

  // Who am I, who is the peer.
  useEffect(() => {
    api.me().then((p) => {
      setMe(p.id);
      const [, a, b] = roomId.split(':');
      const peerId = a === p.id ? b : a;
      if (peerId) api.user(peerId).then(setPeer).catch(() => {});
    }).catch((e) => setError(e.message));
  }, [roomId]);

  // Socket lifecycle with reconnect; HTTP history as a fallback when the socket never joins.
  useEffect(() => {
    closed.current = false;
    let ping: number | undefined;
    let retry: number | undefined;
    let joined = false;

    const connect = () => {
      if (closed.current) return;
      setStatus('connecting');
      const sock = new WebSocket(chatSocketUrl(roomId));
      ws.current = sock;
      sock.onopen = () => sock.send(JSON.stringify({ type: 'join', token: getToken() }));
      sock.onmessage = (ev) => {
        const data = JSON.parse(ev.data);
        switch (data.type) {
          case 'joined': joined = true; setStatus('live'); break;
          case 'history': setMessages(data.messages); api.markSeen(roomId).catch(() => {}); break;
          case 'message': {
            const { type: _t, ...m } = data;
            setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
            if (m.senderId !== me) sock.send(JSON.stringify({ type: 'seen' }));
            break;
          }
          case 'error': if (data.code !== 'rate_limited') setError(data.message); else setError('Slow down a little'); break;
        }
      };
      sock.onclose = () => {
        setStatus('offline');
        if (!joined) api.chatMessages(roomId).then((r) => setMessages(r.messages)).catch(() => {});
        if (!closed.current) retry = window.setTimeout(connect, RECONNECT_MS);
      };
      sock.onerror = () => sock.close();
    };
    connect();
    ping = window.setInterval(() => { if (ws.current?.readyState === WebSocket.OPEN) ws.current.send('{"type":"ping"}'); }, PING_MS);
    return () => {
      closed.current = true;
      window.clearInterval(ping);
      window.clearTimeout(retry);
      ws.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  useEffect(() => { bottom.current?.scrollIntoView({ block: 'end' }); }, [messages.length]);

  const send = async () => {
    const body = draft.trim();
    if (!body) return;
    setDraft('');
    setError(null);
    if (ws.current?.readyState === WebSocket.OPEN && status === 'live') {
      ws.current.send(JSON.stringify({ type: 'send', body }));
      return;
    }
    try {
      const m = await api.sendMessageHttp(roomId, body);
      setMessages((prev) => [...prev, m]);
    } catch (e: any) { setError(e.message); setDraft(body); }
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="chat">
      {peer && (
        <Link to={`/users/${peer.id}`} className="row" style={{ color: 'inherit', marginBottom: 8 }}>
          <Avatar url={peer.photoUrl} name={peer.displayName} size="sm" />
          <strong>{peer.displayName}</strong>
          {peer.city && <span className="muted small">· {peer.city}</span>}
        </Link>
      )}
      {last && <Link to={last.path} className="small" style={{ marginBottom: 6 }}>‹ Back to results: {last.name}</Link>}
      {status !== 'live' && <div className="status-line">{status === 'connecting' ? 'Connecting…' : 'Offline — messages will still be saved'}</div>}
      <div className="thread">
        {messages.length === 0 && <div className="empty">Say hi 👋</div>}
        {messages.map((m) => (
          <div key={m.id} className={`bubble ${m.senderId === me ? 'mine' : 'theirs'}`}>
            {m.body}
            <time>{new Date(m.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</time>
          </div>
        ))}
        <div ref={bottom} />
      </div>
      {error && <div className="error">{error}</div>}
      <div className="composer">
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={onKey} placeholder="Message…" maxLength={1000} rows={1} />
        <button className="btn" onClick={send} disabled={!draft.trim()} aria-label="Send"><Icon name="send" size={22} /></button>
      </div>
    </div>
  );
}
